/**
 * Was die Blender-Karten im Chat sagen — eine Zeile, die Rohdaten nur aufgeklappt. (07.10.2026)
 *
 * Martins Bild vom 07.10.2026: „Blender · Befehl ausgeführt · 8-mal {'n': 45, 'objs': [('Bank_Bein_-0.6', None,
 * 1.93, 2.06, 0.21), …]}“ — die Karte zeigte die letzten Zeilen der Python-Ausgabe roh. Jetzt steht dort eine Zeile
 * („ausgeführt · vorher/nachher · Ausgabe mit 45 Einträgen“), die rohe Ausgabe bleibt vollständig erreichbar
 * (aufklappen). Nichts wird verschwiegen: Ein Fehler kommt gar nicht hierher — die Karte zeigt ihn vorher als Fehler.
 *
 * Reine Funktionen ohne DOM (getestet in tests/frontend/blender-karte-zeile.test.ts).
 */

/** Bis hierhin steht eine Ausgabe unverändert in der Zeile; was länger ist, wandert in die Details. */
const AUSGABE_KURZ = 80;

/**
 * Zeilen, die nach einem Problem klingen, bleiben in der Kurzzeile sichtbar — auch wenn die Ausgabe sonst eingeklappt
 * ist. Ein „FEHLER: …“, das Vesperas eigener Code druckt, ist kein Blender-Fehler (der Lauf gilt als gelungen) und
 * stünde sonst nur im Aufklappteil (Prüfbefund 07.10.2026, K9).
 */
const PROBLEM = /fehler|error|warn|traceback/i;

type O = Record<string, unknown>;

const zahlwort = (n: number, eins: string, viele: string): string => `${n} ${n === 1 ? eins : viele}`;

/**
 * Wie viele Einträge hat die erste Liste in einer Python- oder JSON-Ausgabe? `{'n': 45, 'objs': [(…), (…)]}` → 2.
 * null, wenn keine vollständige Liste darin steht (abgeschnitten, kein Klammertext).
 */
export function eintraegeDerErstenListe(text: string): number | null {
  const start = text.indexOf("[");
  if (start < 0) return null;
  let tiefe = 0;
  let zeichen: string | null = null;
  let kommas = 0;
  let inhalt = false;
  for (let i = start; i < text.length; i++) {
    const c = text.charAt(i);
    if (zeichen) {
      if (c === "\\") i++;
      else if (c === zeichen) zeichen = null;
      continue;
    }
    if (c === "'" || c === '"') {
      zeichen = c;
      if (tiefe === 1) inhalt = true;
      continue;
    }
    if (c === "[" || c === "(" || c === "{") {
      tiefe++;
      if (tiefe === 2) inhalt = true;
      continue;
    }
    if (c === "]" || c === ")" || c === "}") {
      tiefe--;
      if (tiefe === 0) return inhalt ? kommas + 1 : 0;
      continue;
    }
    if (tiefe === 1 && c === ",") kommas++;
    else if (tiefe === 1 && c.trim()) inhalt = true;
  }
  return null;
}

/** Eine Python-Ausgabe → (zusammenfassende Zeile, Rohtext zum Aufklappen | null). */
export function pythonAusgabe(r: O): { zeile: string; roh: string | null } {
  const aus = typeof r.ausgabe === "string" ? r.ausgabe.trim() : "";
  const ergebnis = r.ergebnis === undefined || r.ergebnis === null ? "" : JSON.stringify(r.ergebnis);
  const teile: string[] = [];
  const kurz = aus && aus.length <= AUSGABE_KURZ && !aus.includes("\n") && !/^[[{(]/.test(aus);
  teile.push(kurz ? aus : "ausgeführt");
  if (r.quelle === "blender-vorher-nachher") teile.push("vorher/nachher");
  else if (r.bild === "fehlt") teile.push("ohne Kontrollbild");
  const gesamt = (r.aenderungen as O | undefined)?.gesamt as O | undefined;
  if (gesamt) {
    const n = (k: string): number => {
      const v = gesamt[k];
      return typeof v === "number" ? v : 0;
    };
    if (n("neu")) teile.push(`${n("neu")} neu`);
    if (n("veraendert")) teile.push(`${n("veraendert")} verändert`);
    if (n("entfernt")) teile.push(`${n("entfernt")} entfernt`);
  }
  const roh = [kurz ? "" : aus, ergebnis.length > 2 ? `ergebnis = ${ergebnis}` : ""].filter(Boolean).join("\n\n");
  if (!roh) return { zeile: teile.join(" · "), roh: null };
  const zeilen = aus ? aus.split("\n").length : 0;
  const probleme = aus.split("\n").map((z) => z.trim()).filter((z) => PROBLEM.test(z));
  const n = eintraegeDerErstenListe(aus || ergebnis);
  teile.push(
    n !== null && zeilen <= 1
      ? `Ausgabe mit ${zahlwort(n, "Eintrag", "Einträgen")}`
      : zeilen > 1
        ? `Ausgabe mit ${zeilen} Zeilen`
        : `Ausgabe (${roh.length.toLocaleString("de-DE")} Zeichen)`,
  );
  const [erste] = probleme;
  if (erste) {
    const kurzZeile = erste.length > 120 ? `${erste.slice(0, 119)}…` : erste;
    teile.push(probleme.length > 1 ? `${kurzZeile} (+${probleme.length - 1})` : kurzZeile);
  }
  return { zeile: teile.join(" · "), roh };
}

/** Die Prüfung in einer Zeile; der Befund (Namen, Zentimeter) zum Aufklappen. */
export function pruefungZeile(r: O): { zeile: string; roh: string | null } {
  const z = (r.zahlen ?? {}) as O;
  const n = (k: string): number => {
    const v = z[k];
    return typeof v === "number" ? v : 0;
  };
  const teile = [typeof z.geprueft === "number" ? zahlwort(z.geprueft, "Teil", "Teile") : "geprüft"];
  const gruende = Array.isArray(r.nicht_belegt) ? r.nicht_belegt.filter((g): g is string => typeof g === "string") : [];
  if (r.belegt === false) {
    // Kein „nichts schwebt“, wenn nichts (oder nicht alles) geprüft wurde — Prüfbefund 07.10.2026, K1.
    teile.splice(0, 1, `nicht belegt: ${gruende.join(", ") || "unvollständig"}`);
    if (n("schwebt")) teile.push(`${n("schwebt")} schwebend`);
    if (n("versunken")) teile.push(`${n("versunken")} versunken`);
  } else if (r.sauber === true) teile.push("nichts schwebt, nichts versunken");
  else {
    if (n("schwebt")) teile.push(`${n("schwebt")} schwebend`);
    if (n("versunken")) teile.push(`${n("versunken")} versunken`);
  }
  if (n("durchdringt")) teile.push(`${n("durchdringt")} ineinander`);
  return { zeile: teile.join(" · "), roh: typeof r.zusammenfassung === "string" && r.zusammenfassung ? r.zusammenfassung : null };
}

/** Das Aufsetzen in einer Zeile; was sich bewegt hat, zum Aufklappen. */
export function aufsetzenZeile(r: O): { zeile: string; roh: string | null } {
  const bewegt = typeof r.bewegt === "number" ? r.bewegt : 0;
  const danach = (r.danach ?? {}) as O;
  const teile = [bewegt ? `${zahlwort(bewegt, "Teil", "Teile")} aufgesetzt` : "nichts bewegt"];
  if (typeof danach.schwebt === "number" && typeof danach.versunken === "number") {
    teile.push(
      danach.schwebt || danach.versunken
        ? `danach ${danach.schwebt} schwebend, ${danach.versunken} versunken`
        : "danach nichts schwebend",
    );
  }
  if (r.rueckgaengig === false) teile.push("ohne Rückgängig-Schritt");
  return { zeile: teile.join(" · "), roh: typeof r.zusammenfassung === "string" && r.zusammenfassung ? r.zusammenfassung : null };
}
